import i18n from 'i18next'
import { initReactI18next, useTranslation } from 'react-i18next'

// English translations
import enCommon from './resources/en/common.json'
import enFiles from './resources/en/files.json'
import enCoreUi from './resources/en/core-ui.json'
import enAgent from './resources/en/agent.json'
import enAuth from './resources/en/auth.json'
import enKeybook from './resources/en/keybook.json'
import enBilling from './resources/en/billing.json'
import enCollab from './resources/en/collab.json'

// Simplified Chinese translations
import zhCNCommon from './resources/zh-CN/common.json'
import zhCNFiles from './resources/zh-CN/files.json'
import zhCNCoreUi from './resources/zh-CN/core-ui.json'
import zhCNAgent from './resources/zh-CN/agent.json'
import zhCNAuth from './resources/zh-CN/auth.json'
import zhCNKeybook from './resources/zh-CN/keybook.json'
import zhCNBilling from './resources/zh-CN/billing.json'
import zhCNCollab from './resources/zh-CN/collab.json'

// Traditional Chinese translations
import zhTWCommon from './resources/zh-TW/common.json'
import zhTWFiles from './resources/zh-TW/files.json'
import zhTWCoreUi from './resources/zh-TW/core-ui.json'
import zhTWAgent from './resources/zh-TW/agent.json'
import zhTWAuth from './resources/zh-TW/auth.json'
import zhTWKeybook from './resources/zh-TW/keybook.json'
import zhTWBilling from './resources/zh-TW/billing.json'
import zhTWCollab from './resources/zh-TW/collab.json'

// Malay translations
import msCommon from './resources/ms/common.json'
import msFiles from './resources/ms/files.json'
import msCoreUi from './resources/ms/core-ui.json'
import msAgent from './resources/ms/agent.json'
import msAuth from './resources/ms/auth.json'
import msKeybook from './resources/ms/keybook.json'
import msBilling from './resources/ms/billing.json'
import msCollab from './resources/ms/collab.json'

export const resources = {
  en: {
    common: enCommon,
    files: enFiles,
    'core-ui': enCoreUi,
    agent: enAgent,
    auth: enAuth,
    keybook: enKeybook,
    billing: enBilling,
    collab: enCollab,
  },
  'zh-CN': {
    common: zhCNCommon,
    files: zhCNFiles,
    'core-ui': zhCNCoreUi,
    agent: zhCNAgent,
    auth: zhCNAuth,
    keybook: zhCNKeybook,
    billing: zhCNBilling,
    collab: zhCNCollab,
  },
  'zh-TW': {
    common: zhTWCommon,
    files: zhTWFiles,
    'core-ui': zhTWCoreUi,
    agent: zhTWAgent,
    auth: zhTWAuth,
    keybook: zhTWKeybook,
    billing: zhTWBilling,
    collab: zhTWCollab,
  },
  ms: {
    common: msCommon,
    files: msFiles,
    'core-ui': msCoreUi,
    agent: msAgent,
    auth: msAuth,
    keybook: msKeybook,
    billing: msBilling,
    collab: msCollab,
  },
} as const

export const supportedLanguages = [
  { code: 'en', name: 'English' },
  { code: 'ms', name: 'Bahasa Malaysia' },
  { code: 'zh-CN', name: '简体中文' },
  { code: 'zh-TW', name: '繁體中文' },
] as const

export type SupportedLanguage = (typeof supportedLanguages)[number]['code']

/**
 * Interpolation variables available to EVERY translated string without the call
 * site passing them (white-label support). Resource JSON says
 * `{{productName}}` and gets the running brand's name.
 *
 * WHY A MUTABLE SINGLETON: i18next's Interpolator captures this object BY
 * REFERENCE during `init` (`this.defaultVariables = iOpts.defaultVariables`),
 * so mutating it in place is visible to every later `t()` call — whereas
 * REPLACING `i18n.options.interpolation.defaultVariables` would not be, because
 * the interpolator still holds the old reference. Hence
 * `setBrandInterpolationVars` mutates and never reassigns.
 *
 * The default is Papan's own name so that any `t()` before the brand resolves
 * still renders a sensible string rather than a raw `{{productName}}`.
 * `shared/brand`'s DEFAULT_BRAND is NOT imported here — @papan/i18n must stay
 * free of that dependency (marketing and other consumers use it standalone), so
 * the literal is duplicated and pinned by a test in shared/brand.
 */
const brandInterpolationVars: Record<string, string> = {
  productName: 'Papan',
  productTagline: 'Any file is a database',
}

/**
 * Point i18n's `{{productName}}` / `{{productTagline}}` at the active brand.
 * Called by the SPA once the brand snapshot settles (and again if it changes).
 * Safe to call before or after `init`.
 */
export function setBrandInterpolationVars(vars: { productName?: string; productTagline?: string }): void {
  const nextName = vars.productName || brandInterpolationVars.productName
  const nextTagline = vars.productTagline || brandInterpolationVars.productTagline
  if (
    nextName === brandInterpolationVars.productName &&
    nextTagline === brandInterpolationVars.productTagline
  ) return  // no-op: don't churn re-renders on a repeat resolve

  brandInterpolationVars.productName = nextName
  brandInterpolationVars.productTagline = nextTagline

  // Mutating the variables changes what FUTURE t() calls produce, but already-
  // mounted components hold rendered strings and would keep showing the old
  // product name until something else re-rendered them. react-i18next
  // re-renders its consumers on `languageChanged`, so emit it to flush them.
  // This is an event emit, NOT changeLanguage() — no re-detection and no
  // language-cache write, so the user's chosen language is untouched.
  try { i18n.emit('languageChanged', i18n.language) } catch { /* pre-init */ }
}

/**
 * The key `LanguageDetector` used to read and cache. Kept as a named constant
 * even though nothing detects from it any more, because it is still READ by two
 * unrelated call sites and is overwritten below for their benefit.
 */
export const LANGUAGE_LS_KEY = 'papan-language'

/**
 * ⚠ TEMPORARY (issue #369, lane A, A2.3) — every user is reset to English.
 *
 * ── This is a RESET, not a hide ─────────────────────────────────────────
 *
 * Removing the Language page would not have been enough. `LanguageDetector` ran
 * with `order: ['localStorage', 'navigator']`, `caches: ['localStorage']`,
 * `lookupLocalStorage: 'papan-language'` — so a browser still holding
 * `papan-language='zh-TW'`, or merely REPORTING a Chinese navigator locale,
 * would keep booting the whole app in Chinese with no UI left to change it
 * back. Detection is therefore switched off and the language is pinned.
 *
 * ── Why the key is OVERWRITTEN rather than deleted ──────────────────────
 *
 * Two call sites outside this package still read it directly and neither knows
 * about i18next: `UniverModifier.tsx` reads it twice to pick the spreadsheet
 * editor's own locale, falling back to `navigator.language`. Deleting the key
 * would send those to the navigator and leave the spreadsheet in Chinese inside
 * an English app. Overwriting with `'en'` gives every such reader — including an
 * older instance still serving during a rolling deploy — the right answer
 * without either of them having to know this happened.
 *
 * ── Restoring it later ──────────────────────────────────────────────────
 *
 * Deleting the `lng: 'en'` pin is NOT the restoration. It would revive whatever
 * stale cached preference is still sitting on each user's device (and the
 * navigator fallback for everyone else), silently, with no way to change it if
 * the UI has not come back in the same change. Re-enabling means, together and
 * deliberately: put `LanguageDetector` and the `detection` block back, remove
 * the overwrite below, AND restore a language selector — plus decide whether
 * the preference belongs on `users.settings` this time rather than in a
 * device-local key. The locale RESOURCES are all still here and shipping; only
 * the ability to select one is gone.
 */
try {
  if (typeof localStorage !== 'undefined') localStorage.setItem(LANGUAGE_LS_KEY, 'en')
} catch { /* storage blocked (private mode, embedded webview) — the pin below still holds */ }

i18n
  .use(initReactI18next)
  .init({
    resources,
    // TEMPORARY — see the block above before removing. Not a default: with no
    // detector this is the language, and it is what makes a stale cached
    // preference inert rather than merely unselectable.
    lng: 'en',
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common', 'files', 'core-ui', 'agent', 'auth', 'keybook', 'billing', 'collab'],
    interpolation: {
      escapeValue: false,
      defaultVariables: brandInterpolationVars,
    },
  })

export { useTranslation }
export default i18n
